import { useCallback, useEffect, useState } from "react";
import { Search, UserPlus, Pencil, UserX, Users, Loader2, RefreshCw, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import EmployeeTerritoryGps from "./EmployeeTerritoryGps";

const emptyForm = {
  name: "",
  phone: "",
  designation: "",
  territory: "",
  area_assignments: [],
  temporary_area_assignments: [],
  status: "active",
};

export default function EmployeesTab() {
  const [employees, setEmployees] = useState([]);
  const [configuredTerritories, setConfiguredTerritories] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [areaPicker, setAreaPicker] = useState({ state_id: "", city_id: "", area_id: "" });
  const [temporaryPicker, setTemporaryPicker] = useState({
    state_id: "", city_id: "", area_id: "", start_date: "", end_date: "",
  });
  const [newPermanentAreaName, setNewPermanentAreaName] = useState("");
  const [newTemporaryAreaName, setNewTemporaryAreaName] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const defaultPermissions = {
    add_vendors: true,
    view_vendors: true,
    edit_own_vendors: true,
    edit_all_vendors: false,
    approve_verify_vendors: false,
    delete_vendors: false,
  };
  const [permissionEmployee, setPermissionEmployee] = useState(null);
  const [permissionForm, setPermissionForm] = useState(defaultPermissions);

  const [performanceEmployee, setPerformanceEmployee] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [performancePage, setPerformancePage] = useState(1);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState("");

  const loadPerformance = async (employee, page = 1) => {
    setPerformanceEmployee(employee);
    setPerformancePage(page);
    setPerformanceLoading(true);
    setPerformanceError("");
    try {
      const { data } = await api.get(
        `/admin/employees/${encodeURIComponent(employee.employee_id)}/performance`,
        { params: { page, page_size: 25 } }
      );
      setPerformanceData(data);
    } catch (e) {
      setPerformanceError(e.response?.data?.detail || "Unable to load employee performance.");
      setPerformanceData(null);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/employees");
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load employees. Check your admin access.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  useEffect(() => {
    api.get("/locations/tree")
      .then(({ data }) => setLocationTree(Array.isArray(data) ? data : []))
      .catch(() => setLocationTree([]));
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setAreaPicker({ state_id: "", city_id: "", area_id: "" });
    setTemporaryPicker({ state_id: "", city_id: "", area_id: "", start_date: "", end_date: "" });
    setError("");
    setNotice("");
    setShowForm(true);
  };

  const startEdit = (employee) => {
    setEditing(employee.employee_id);
    setForm({
      name: employee.name || "",
      phone: employee.phone || "",
      designation: employee.designation || "",
      territory: employee.territory || "",
      area_assignments: employee.area_assignments || [],
      temporary_area_assignments: employee.temporary_area_assignments || [],
      status: employee.status || "active",
    });
    setAreaPicker({ state_id: "", city_id: "", area_id: "" });
    setTemporaryPicker({ state_id: "", city_id: "", area_id: "", start_date: "", end_date: "" });
    setError("");
    setNotice("");
    setShowForm(true);
  };

  const assignmentStates = Array.from(
    new Map(
      locationTree
        .filter((city) => city && city.state && city.parent_id)
        .map((city) => [city.parent_id, { id: city.parent_id, name: city.state }])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  const permanentCities = locationTree.filter(
    (city) => city && city.type === "city" && city.parent_id === areaPicker.state_id
  );
  const permanentCity = locationTree.find((city) => city.id === areaPicker.city_id);
  const permanentAreas = permanentCity?.areas || [];

  const temporaryCities = locationTree.filter(
    (city) => city && city.type === "city" && city.parent_id === temporaryPicker.state_id
  );
  const temporaryCity = locationTree.find((city) => city.id === temporaryPicker.city_id);
  const temporaryAreas = temporaryCity?.areas || [];

  const createAreaForCity = async (city, rawName) => {
    const name = rawName.trim();
    if (!city || !name) throw new Error("Select a city and enter an area name.");

    const existing = (city.areas || []).find(
      (item) => item.name?.trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) return existing;

    const { data } = await api.post("/admin/locations", {
      type: "area", name, parent_id: city.id, active: true, order: 100,
    });
    const response = await api.get("/locations/tree");
    setLocationTree(Array.isArray(response.data) ? response.data : []);
    return data;
  };

  const addPermanentArea = async () => {
    try {
      const city = permanentCity;
      const area = areaPicker.area_id
        ? permanentAreas.find((item) => item.id === areaPicker.area_id)
        : await createAreaForCity(city, newPermanentAreaName);
      if (!city || !area) throw new Error("Select or enter an area.");
    if ((form.area_assignments || []).some((item) => item.area_id === area.id)) {
      setError("This area is already assigned permanently.");
      return;
    }
    change("area_assignments", [
      ...(form.area_assignments || []),
      {
        area_id: area.id, area_name: area.name,
        city_id: city.id, city_name: city.name,
        state_id: city.parent_id, state_name: city.state,
      },
    ]);
    setAreaPicker((old) => ({ ...old, area_id: "" }));
    setNewPermanentAreaName("");
    setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Unable to add area.");
    }
  };

  const addTemporaryArea = async () => {
    const city = temporaryCity;
    if (!city || !temporaryPicker.start_date || !temporaryPicker.end_date) {
      setError("Select a city and both assignment dates.");
      return;
    }
    const area = temporaryPicker.area_id
      ? temporaryAreas.find((item) => item.id === temporaryPicker.area_id)
      : await createAreaForCity(city, newTemporaryAreaName);
    if (!area) {
      setError("Select a temporary area and both assignment dates.");
      return;
    }
    if (temporaryPicker.end_date < temporaryPicker.start_date) {
      setError("Temporary assignment end date cannot be before its start date.");
      return;
    }
    change("temporary_area_assignments", [
      ...(form.temporary_area_assignments || []),
      {
        area_id: area.id, area_name: area.name,
        city_id: city.id, city_name: city.name,
        state_id: city.parent_id, state_name: city.state,
        start_date: temporaryPicker.start_date,
        end_date: temporaryPicker.end_date,
      },
    ]);
    setTemporaryPicker((old) => ({ ...old, area_id: "", start_date: "", end_date: "" }));
    setNewTemporaryAreaName("");
    setError("");
  };

  const change = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const saveEmployee = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    try {
      if (editing) {
        const { status, ...editable } = form;
        await api.put(`/admin/employees/${encodeURIComponent(editing)}`, editable);
        if (status === "inactive") {
          await api.put(`/admin/employees/${encodeURIComponent(editing)}`, { status: "inactive" });
        } else if (status === "active") {
          await api.put(`/admin/employees/${encodeURIComponent(editing)}`, { status: "active" });
        }
        setNotice("Employee updated successfully.");
      } else {
        await api.post("/admin/employees", form);
        setNotice("Employee created successfully.");
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditing(null);
      await loadEmployees();
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to save employee.");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (employee) => {
    if (!window.confirm(`Deactivate ${employee.name} (${employee.employee_id})? They will no longer be able to sign in.`)) return;
    setError("");
    setNotice("");
    try {
      await api.post(`/admin/employees/${encodeURIComponent(employee.employee_id)}/deactivate`);
      setNotice(`${employee.name} has been deactivated.`);
      await loadEmployees();
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to deactivate employee.");
    }
  };

  const openPermissions = (employee) => {
    setPermissionEmployee(employee);
    setPermissionForm({ ...defaultPermissions, ...(employee.permissions || {}), delete_vendors: false });
    setError("");
    setNotice("");
  };

  const savePermissions = async () => {
    if (!permissionEmployee) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { delete_vendors, ...permissions } = permissionForm;
      await api.put(
        `/admin/employees/${encodeURIComponent(permissionEmployee.employee_id)}/permissions`,
        permissions
      );
      setNotice(`Permissions updated for ${permissionEmployee.name}.`);
      setPermissionEmployee(null);
      await loadEmployees();
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to update employee permissions.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = employees.filter((employee) =>
    [employee.employee_id, employee.name, employee.phone, employee.designation, employee.territory, employee.status]
      .some((value) => String(value || "").toLowerCase().includes(search.toLowerCase().trim()))
  );

  const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100";
  const activeCount = employees.filter((e) => e.status === "active").length;

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold">Employee Management</h2>
          <p className="mt-1 text-sm text-muted-foreground">Create employee accounts and manage their access.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadEmployees} disabled={loading} className="rounded-xl">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={startCreate} className="rounded-xl">
            <UserPlus className="mr-2 h-4 w-4" /> Add Employee
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Total employees</p>
          <p className="mt-1 text-2xl font-bold">{employees.length}</p>
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Active</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{activeCount}</p>
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <p className="text-sm text-muted-foreground">Inactive</p>
          <p className="mt-1 text-2xl font-bold">{employees.length - activeCount}</p>
        </div>
      </div>


      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div>}

      {performanceEmployee && (
        <div className="space-y-5 rounded-2xl border bg-card p-4 sm:p-6" data-testid="employee-performance-panel">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Employee Performance</p>
              <h3 className="text-xl font-bold">{performanceEmployee.name}</h3>
              <p className="text-sm text-muted-foreground">
                {performanceEmployee.employee_id} · {performanceEmployee.designation || "Employee"} · {performanceEmployee.territory || "Territory not set"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => loadPerformance(performanceEmployee, performancePage)} disabled={performanceLoading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${performanceLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setPerformanceEmployee(null); setPerformanceData(null); setPerformanceError(""); }}>
                Close
              </Button>
            </div>
          </div>

          {performanceError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{performanceError}</div>}

          {performanceLoading && !performanceData ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading performance report...
            </div>
          ) : performanceData ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Total onboarded", performanceData.stats?.total_onboarded],
                  ["Drafts", performanceData.stats?.drafts],
                  ["Submitted", performanceData.stats?.submitted],
                  ["Under review", performanceData.stats?.under_review],
                  ["Approved / verified", performanceData.stats?.approved_verified],
                  ["Corrections requested", performanceData.stats?.corrections_requested],
                  ["Rejected", performanceData.stats?.rejected],
                  ["Suspended", performanceData.stats?.suspended],
                  ["Vendors updated", performanceData.stats?.updated_count],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border bg-background p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums">{value ?? 0}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border p-4">
                <h4 className="font-semibold">Additional performance modules</h4>
                <p className="mt-1 text-xs text-muted-foreground">These modules need recording workflows before reports can display actual metrics.</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    ["Attendance", "attendance"],
                    ["GPS field visits", "gps_field_visits"],
                    ["Working hours", "working_hours"],
                    ["Targets", "targets"],
                    ["Activity history", "activity_history"],
                  ].map(([label, key]) => (
                    <div key={key} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
                      <span>{label}</span>
                      <span className="text-xs font-medium text-muted-foreground">Not recorded</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold">Vendor onboarding history</h4>
                  <p className="text-xs text-muted-foreground">Vendors originally created by this employee. Last-updated attribution is counted separately.</p>
                </div>
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-3">Business</th>
                        <th className="px-3 py-3">Category</th>
                        <th className="px-3 py-3">Location</th>
                        <th className="px-3 py-3">Registered</th>
                        <th className="px-3 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(performanceData.vendors || []).length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No attributed vendor records found.</td></tr>
                      ) : performanceData.vendors.map((vendor) => (
                        <tr key={vendor.id}>
                          <td className="px-3 py-3 font-medium">{vendor.business_name || "Unnamed vendor"}</td>
                          <td className="px-3 py-3">{vendor.category_name || vendor.category_slug || "—"}</td>
                          <td className="px-3 py-3">{[vendor.area, vendor.city, vendor.state].filter(Boolean).join(", ") || "—"}</td>
                          <td className="px-3 py-3 text-xs whitespace-nowrap">{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString("en-IN") : "—"}</td>
                          <td className="px-3 py-3">
                            <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium">{vendor.status || "draft"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    {performanceData.pagination?.total || 0} vendor records · Page {performanceData.pagination?.page || 1} of {Math.max(1, performanceData.pagination?.pages || 1)}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={performanceLoading || performancePage <= 1} onClick={() => loadPerformance(performanceEmployee, performancePage - 1)}>
                      <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                    </Button>
                    <Button size="sm" variant="outline" disabled={performanceLoading || performancePage >= (performanceData.pagination?.pages || 1)} onClick={() => loadPerformance(performanceEmployee, performancePage + 1)}>
                      Next <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : !performanceError ? (
            <div className="py-6 text-sm text-muted-foreground">Performance report is not available.</div>
          ) : null}
        </div>
      )}

      {showForm && (
        <form onSubmit={saveEmployee} className="space-y-4 rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editing ? `Edit ${editing}` : "Create employee account"}</h3>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">Employee name
              <input className={inputClass + " mt-1"} required minLength={2} maxLength={100} value={form.name} onChange={(e) => change("name", e.target.value)} />
            </label>
            <label className="text-sm font-medium">Mobile number
              <input className={inputClass + " mt-1"} required inputMode="numeric" pattern="[0-9+() -]{10,16}" maxLength={16} value={form.phone} onChange={(e) => change("phone", e.target.value)} placeholder="10-digit mobile number" />
            </label>
            <label className="text-sm font-medium">Designation
              <input className={inputClass + " mt-1"} required maxLength={100} value={form.designation} onChange={(e) => change("designation", e.target.value)} placeholder="e.g. Vendor Onboarding Executive" />
            </label>
            <div className="space-y-3 rounded-xl border p-3 sm:col-span-2">
              <div>
                <h4 className="font-semibold">Permanent Area Assignments</h4>
                <p className="text-xs text-muted-foreground">An employee can work in all permanently assigned areas.</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <select className={inputClass} value={areaPicker.state_id}
                  onChange={(e) => setAreaPicker({ state_id: e.target.value, city_id: "", area_id: "" })}>
                  <option value="">Select state</option>
                  {assignmentStates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select className={inputClass} value={areaPicker.city_id} disabled={!areaPicker.state_id}
                  onChange={(e) => setAreaPicker((old) => ({ ...old, city_id: e.target.value, area_id: "" }))}>
                  <option value="">Select city</option>
                  {permanentCities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select className={inputClass} value={areaPicker.area_id} disabled={!areaPicker.city_id}
                  onChange={(e) => setAreaPicker((old) => ({ ...old, area_id: e.target.value }))}>
                  <option value="">Select area</option>
                  {permanentAreas.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input className={inputClass + " flex-1"}
                  placeholder="Enter a new area / locality"
                  maxLength={100}
                  value={newPermanentAreaName}
                  onChange={(e) => setNewPermanentAreaName(e.target.value)}
                  disabled={!areaPicker.city_id}
                />
                <Button type="button" variant="outline" onClick={addPermanentArea}
                  disabled={!areaPicker.area_id && (!areaPicker.city_id || !newPermanentAreaName.trim())}>
                  Add permanent area
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(form.area_assignments || []).map((item) => (
                  <span key={item.area_id} className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs">
                    {item.state_name} · {item.city_name} · {item.area_name}
                    <button type="button" className="font-bold text-red-600"
                      onClick={() => change("area_assignments", form.area_assignments.filter((x) => x.area_id !== item.area_id))}
                      aria-label={`Remove ${item.area_name}`}>×</button>
                  </span>
                ))}
                {!(form.area_assignments || []).length && <span className="text-xs text-muted-foreground">No permanent areas added yet.</span>}
              </div>
            </div>

            <div className="space-y-3 rounded-xl border p-3 sm:col-span-2">
              <div>
                <h4 className="font-semibold">Temporary Area Assignments</h4>
                <p className="text-xs text-muted-foreground">Temporary areas are authorized only between their start and end dates.</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <select className={inputClass} value={temporaryPicker.state_id}
                  onChange={(e) => setTemporaryPicker({ state_id: e.target.value, city_id: "", area_id: "", start_date: "", end_date: "" })}>
                  <option value="">Select state</option>
                  {assignmentStates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select className={inputClass} value={temporaryPicker.city_id} disabled={!temporaryPicker.state_id}
                  onChange={(e) => setTemporaryPicker((old) => ({ ...old, city_id: e.target.value, area_id: "" }))}>
                  <option value="">Select city</option>
                  {temporaryCities.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select className={inputClass} value={temporaryPicker.area_id} disabled={!temporaryPicker.city_id}
                  onChange={(e) => setTemporaryPicker((old) => ({ ...old, area_id: e.target.value }))}>
                  <option value="">Select area</option>
                  {temporaryAreas.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <input className={inputClass}
                placeholder="Enter a new area / locality"
                maxLength={100}
                value={newTemporaryAreaName}
                onChange={(e) => setNewTemporaryAreaName(e.target.value)}
                disabled={!temporaryPicker.city_id}
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs font-medium">Start date
                  <input className={inputClass + " mt-1"} type="date" value={temporaryPicker.start_date}
                    onChange={(e) => setTemporaryPicker((old) => ({ ...old, start_date: e.target.value }))} />
                </label>
                <label className="text-xs font-medium">End date
                  <input className={inputClass + " mt-1"} type="date" min={temporaryPicker.start_date || undefined}
                    value={temporaryPicker.end_date}
                    onChange={(e) => setTemporaryPicker((old) => ({ ...old, end_date: e.target.value }))} />
                </label>
              </div>
              <Button type="button" variant="outline" onClick={addTemporaryArea}
                disabled={(!temporaryPicker.area_id && (!temporaryPicker.city_id || !newTemporaryAreaName.trim())) || !temporaryPicker.start_date || !temporaryPicker.end_date}>
                Add temporary area
              </Button>
              <div className="space-y-2">
                {(form.temporary_area_assignments || []).map((item, index) => (
                  <div key={`${item.area_id}-${item.start_date}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2 text-xs">
                    <span>{item.state_name} · {item.city_name} · {item.area_name} ({item.start_date} to {item.end_date})</span>
                    <button type="button" className="font-semibold text-red-600"
                      onClick={() => change("temporary_area_assignments", form.temporary_area_assignments.filter((_, i) => i !== index))}>Remove</button>
                  </div>
                ))}
                {!(form.temporary_area_assignments || []).length && <span className="text-xs text-muted-foreground">No temporary areas added.</span>}
              </div>
            </div>

            <label className="text-sm font-medium">Assigned territory
              <select className={inputClass + " mt-1"} required value={form.territory}
                onChange={(e) => change("territory", e.target.value)}>
                <option value="">Select territory</option>
                {[...new Set([
                  ...configuredTerritories,
                  ...employees.map((item) => item.territory).filter(Boolean),
                  ...(form.territory ? [form.territory] : []),
                ])].sort((a, b) => a.localeCompare(b)).map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-muted-foreground">
                Configure additional territories in Territory & GPS Location Management.
              </span>
            </label>
            {editing && <label className="text-sm font-medium">Account status
              <select className={inputClass + " mt-1"} value={form.status} onChange={(e) => change("status", e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>}
          </div>
          <p className="text-xs text-muted-foreground">Employees sign in using their registered mobile number and OTP. Each mobile number must be unique.</p>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="rounded-xl">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : editing ? "Save changes" : "Create employee"}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowForm(false); setError(""); }}>Cancel</Button>
          </div>
        </form>
      )}

      {permissionEmployee && (
        <div className="rounded-2xl border border-violet-200 bg-card p-5 shadow-sm">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">Employee Permissions</h3>
            <p className="text-sm text-muted-foreground">
              {permissionEmployee.name} · {permissionEmployee.employee_id}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["add_vendors", "Add vendors"],
              ["view_vendors", "Search and view vendors"],
              ["edit_own_vendors", "Edit vendors created by this employee"],
              ["edit_all_vendors", "Edit all vendors"],
              ["approve_verify_vendors", "Approve / verify vendors"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 rounded-xl border p-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!permissionForm[key]}
                  onChange={(e) => setPermissionForm((old) => ({ ...old, [key]: e.target.checked }))}
                  className="h-4 w-4 accent-violet-600"
                />
                {label}
              </label>
            ))}
            <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
              Delete vendors — Not permitted for employees
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={savePermissions} disabled={saving}>
              {saving ? "Saving..." : "Save permissions"}
            </Button>
            <Button variant="outline" onClick={() => setPermissionEmployee(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-xl border bg-card px-3">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input className="w-full bg-transparent py-3 text-sm outline-none" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by ID, name, mobile, designation or territory..." />
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading employees...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No employees found</p>
            <p className="mt-1 text-sm text-muted-foreground">{search ? "Try a different search." : "Add your first employee to get started."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Territory</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((employee) => (
                  <tr key={employee.employee_id} className="hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{employee.name}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{employee.employee_id}</div>
                    </td>
                    <td className="px-4 py-3">{employee.phone}</td>
                    <td className="px-4 py-3">{employee.designation}</td>
                    <td className="px-4 py-3">{employee.territory}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${employee.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {employee.status || "unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => loadPerformance(employee, 1)}><Eye className="mr-1.5 h-3.5 w-3.5" /> View Performance</Button>
                        <Button size="sm" variant="outline" onClick={() => startEdit(employee)}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => openPermissions(employee)}>Permissions</Button>
                        {employee.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => deactivate(employee)} className="text-red-600 hover:text-red-700">
                            <UserX className="mr-1.5 h-3.5 w-3.5" /> Deactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EmployeeTerritoryGps onTerritoriesLoaded={setConfiguredTerritories} />
    </section>
  );
}
